import axios from "axios";
import type { RequestError } from "client/api/RequestError";

function isRequestError(error: unknown): error is RequestError {
  if (!error) {
    return false;
  }

  if (typeof error !== "object") {
    return false;
  }

  return "title" in error && "status" in error && "traceId" in error;
}

export interface ErrorContent {
  title: string;
  content: string | Record<string, string[]>;
}

export function getErrorContent(error: unknown): ErrorContent {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data;
    if (!errorResponse) {
      return {
        title: "Something went wrong!",
        content: error.message ?? "Please try again or reach out for assistance.",
      };
    }
    const isKnownError = isRequestError(errorResponse);
    if (isKnownError) {
      return {
        title: errorResponse.title,
        content: errorResponse.detail ?? errorResponse.errors ?? "",
      };
    }
  }

  return {
    title: "Something went wrong!",
    content: error instanceof Error ? error.message : "Please try again or reach out for assistance.",
  };
}

export function getErrorMessage(error: unknown): string {
  const errorContent = getErrorContent(error);

  // If content is a string, return title + content
  if (typeof errorContent.content === "string") {
    return errorContent.content || errorContent.title;
  }

  // If content is an object with errors, format them
  const errorMessages: string[] = [];
  Object.entries(errorContent.content).forEach(([key, messages]) => {
    if (messages && messages.length > 0) {
      errorMessages.push(...messages);
    }
  });

  return errorMessages.length > 0 ? errorMessages.join(". ") : errorContent.title;
}
